import './SearchView.less';

import React, { useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import i18n from '@i18n';
import {
    addSearchTextToHistory,
    clearSearchTextHistory,
    getSearchTextHistory,
    removeSearchTextHistory
} from '../searchHistory';
import { caseAndAccentInsensitiveRegexAnyWords } from 'nike-ui/src/common/utils/regex';
import { fireGAEvent } from '@/analytics';
import { isSearchTextLengthInsufficient } from '../util';
import DeleteIcon from '@/icons/DeleteIcon';
import { defaultComparator } from '@/common/util';
import PopupNg from '@/ui/PopupNg';
import { scrollBody } from '@/common/client';
import {
    COMMUNITY_ANALYSIS_DETAIL,
    CASINO_GAME_DETAIL,
    NIKE1_MATCH,
    NIKE1_TOURNAMENT,
    TURBO_LIVE_MATCH_DETAIL
} from '@/routing/routes';
import { navigate } from '@/routing';
import { updateSearchText } from '@/fullsearch/handlers';
import { searchSuggetionData$, searchSuggetionText$, searchText$ } from '@/fullsearch/subs';
import { searchEventObjectUrlDescription } from 'nike-ui/src/common/sportEvents';
import SearchInfo from '@/fullsearch/views/SearchInfo';
import SearchCasinoGameItem from '@/fullsearch/views/SearchCasinoGameItem';
import SearchBoxTable from '@/fullsearch/views/SearchBoxTable';
import { getSportNameById } from 'nike-ui/src/common/utils/utils';

const ITEMS_DISPLAYED = 3;

function SearchView({ children }) {
    function handleTouch() {
        scrollBody(false);
    }

    return (
        <div className="search-suggestions" onTouchStart={handleTouch}>
            <div className="full-page-shadow" />
            <div className="full-search-row">{children}</div>
        </div>
    );
}

export default SearchView;

//     TOURNAMENT("T"),
//     MATCH("M"),
//     LIVE("L"),
//     ARTICLE("AR"),
//     ANALYSIS("AN"),
//     USER_ANALYSIS("UA"),
//     RESULT("R"),
//     ACTION("AC"),
//     CASINO_GAME("CG");
const types = ['CG', 'L', 'M', 'T', 'UA', 'R'];

const typeComparator = (a, b) => defaultComparator(types.indexOf(a), types.indexOf(b));

export function Suggestions({ betbookActive }) {
    const { subscribe } = useReframe();
    const data = subscribe(searchSuggetionData$) || {};
    const dataSearchText = subscribe(searchSuggetionText$) || '';
    const searchText = subscribe(searchText$) || '';
    const suggetionTypes = Object.keys(data);

    if (isSearchTextLengthInsufficient(searchText)) {
        return <SearchInfo localizationKey={'search.input.size'} />;
    }
    if (searchText !== dataSearchText && suggetionTypes.length === 0) {
        return <SearchInfo localizationKey={'commons.term.loading'} />;
    }
    if (suggetionTypes.length === 0) {
        return <SearchInfo localizationKey={'search.result.empty'} />;
    }

    return (
        <div className="event-box">
            {suggetionTypes
                .sort(typeComparator)
                .filter(betbookActive ? type => type === 'M' : type => types.includes(type))
                .map(type => (
                    <SuggestBoxGroup key={type} itemList={data[type]} type={type} betbookActions={betbookActive} />
                ))}
        </div>
    );
}

function SuggestBoxGroup({ itemList, type, betbookActions }) {
    const [isExpanded, setExpanded] = useState(false);
    const maxItemsDisplayed = isExpanded ? itemList.length : ITEMS_DISPLAYED + (betbookActions ? 2 : 0);

    function showMore() {
        setExpanded(true);
        fireGAEvent('Search', 'Suggestion', 'Zobrazit viac');
    }

    return (
        <div className="suggestion-box">
            <div className="suggestion-title">{i18n('search.result.type.' + type)}</div>
            {itemList
                .slice(0, maxItemsDisplayed)
                .sort((item1, item2) => defaultComparator(item1.sportId, item2.sportId))
                .map(item => (
                    <SuggestBox key={item.id} item={item} betbookActions={betbookActions} />
                ))}
            {!isExpanded && itemList.length > 3 && !betbookActions && (
                <div data-atid="suggestions-showMore" className="blue-link text-center" onClick={showMore}>
                    {i18n('search.result.showMore')}
                </div>
            )}
        </div>
    );
}

function SuggestBox({ item, betbookActions }) {
    const { subscribe } = useReframe();
    const highlight = subscribe(searchText$);

    function openDetailedBets(sportEventId) {
        scrollBody(true);
        PopupNg.dispatcher.open('betbook/searchDetailedBetsDialog', { sportEventId }, 10);
    }

    function redirectToSearchItem(item, highlight) {
        scrollBody(true);
        function doRedirect(args) {
            addSearchTextToHistory(highlight);
            dispatch(['ui.hideSection', 'searchAutoSuggest']);
            dispatch(['ui.hideSection', 'searchBox']);
            dispatch(['ui.hideSection', 'searchDesktop']);
            navigate(...args);
            fireGAEvent('Search', 'Suggestion', 'Detail');
        }

        if (item.type === 'M' || item.type === 'R') {
            doRedirect([NIKE1_MATCH, { eventId: item.id, meta: searchEventObjectUrlDescription(item) }]);
        } else if (item.type === 'L') {
            doRedirect([TURBO_LIVE_MATCH_DETAIL, { sportName: getSportNameById(item.sportId), matchId: item.id }]);
        } else if (item.type === 'T') {
            doRedirect([NIKE1_TOURNAMENT, { boxId: 'bi-' + item.sportId + '-' + item.countryId + '-' + item.id }]);
        } else if (item.type === 'CG') {
            doRedirect([CASINO_GAME_DETAIL, { gameId: item.id }]);
        } else if (item.type === 'UA') {
            doRedirect([COMMUNITY_ANALYSIS_DETAIL, { postId: item.id, postUrlSuffix: item.niceUrl }]);
        }
    }

    const renderOpponents = opponents => (
        <div className="opponents">
            {highlightText(opponents[0], highlight)}
            {opponents.length > 1 && (
                <span>
                    <span className="search-opponents-separator">vs</span>
                    {highlightText(opponents[1], highlight)}
                </span>
            )}
        </div>
    );

    if (item.type === 'CG') {
        return (
            <SearchCasinoGameItem
                gameId={item.id}
                gameName={item.title}
                gameAnotation={item.annotation}
                searchedText={highlight}
                redirectFunction={() => redirectToSearchItem(item, highlight)}
            />
        );
    }

    return (
        <SearchBoxTable>
            <div
                data-atid="sport-icon"
                className={`search-sport-icon sport-icon-${
                    !item.opponents && !item.title ? 'small' : 'mini'
                } icon-${getSportNameById(item.sportId, false)}`}
            />
            <div
                data-atid="suggestions-goTo-selected"
                onClick={betbookActions ? () => openDetailedBets(item.id) : () => redirectToSearchItem(item, highlight)}
                className="search-view-suggestion-content"
            >
                {item.opponents && renderOpponents(item.opponents)}
                {item.tournamentName && (
                    <div className={'league ' + (!item.opponents ? 'only-one' : '')}>
                        {highlightText(item.tournamentName, highlight)}
                    </div>
                )}
                {item.title && <div className="analysis-title">{highlightText(item.title, highlight)}</div>}
                {item.annotation && <div className="post-text">{highlightText(item.annotation, highlight)}</div>}
            </div>
        </SearchBoxTable>
    );
}

export function highlightText(text, highlight) {
    // Split on highlight term and include term into parts, ignore case
    const regex = caseAndAccentInsensitiveRegexAnyWords(highlight);
    const parts = text?.split(regex);
    return (
        <span>
            {parts?.map((part, i) => (
                <span
                    key={i}
                    className={highlight && regex.test(part) ? 'search-heightlight' : ''}
                    dangerouslySetInnerHTML={{ __html: part }}
                />
            ))}{' '}
        </span>
    );
}

export function HistorySuggestions({ onSearchTextUpdate }) {
    const [suggestions, setSuggestions] = useState(getSearchTextHistory());

    function deleteSuggestion(item) {
        removeSearchTextHistory(item);
        setSuggestions(getSearchTextHistory());
        fireGAEvent('Search', 'Suggestion history', 'Odstranit');
    }

    function setSearchText(item) {
        dispatch(updateSearchText(item));
        dispatch(['ui.showSection', 'searchAutoSuggest']);
        onSearchTextUpdate(item);
        fireGAEvent('Vyhladavanie', 'Posledny hladany vyraz', item);
    }

    function clearHistory() {
        clearSearchTextHistory();
        setSuggestions(getSearchTextHistory());
        fireGAEvent('Search', 'Suggestion history', 'Zmazat vsetko');
    }

    return (
        <div className="event-box">
            <div className="suggestion-box">
                <h1>{i18n('search.suggestions.header')}</h1>
                {suggestions && suggestions.length ? (
                    <>
                        {suggestions.map(item => (
                            <SearchBoxTable className="search-box-table-saved px-10 py-8" key={item}>
                                <div className="text" onClick={e => setSearchText(item, e)}>
                                    {item}
                                </div>
                                <div
                                    className="search-box-delete-icon"
                                    data-atid="suggestions-deleteItem"
                                    onClick={e => deleteSuggestion(item, e)}
                                >
                                    <DeleteIcon width={15} height={15} fill={'#808080'} />
                                </div>
                            </SearchBoxTable>
                        ))}
                        <div
                            data-atid="suggestions-deleteAll"
                            className="blue-link text-center py-30"
                            onClick={clearHistory}
                        >
                            {i18n('search.suggestions.delete')}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
}
